import { format, startOfToday } from 'date-fns'
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { shallowEqual } from 'react-redux'
import { Link } from 'react-router-dom'

import { Block, CalendarMonth, Edit } from '@mui/icons-material'
import { Box, Button, Chip, Stack, TableContainer, Typography } from '@mui/material'

import { coreApi } from '../../../../../api/core'
import { GRTable, GRTableColumn } from '../../../../../components/GRTable/GRTable'
import { SortLabel } from '../../../../../components/GRTable/HeaderCell/SortLabel/SortLabel'
import { SortOrder } from '../../../../../components/GRTable/types/SortOrder'
import { GRTooltip } from '../../../../../components/GRTooltip/GRTooltip'
import { GameIcon } from '../../../../../features/game'
import { Game } from '../../../../../features/game/types/Game'
import { TrackedGame, TrackedGameBlueprint } from '../../../../../features/live-events/types/TrackedGame'
import { useAppSelector } from '../../../../../hooks/storeHooks'
import { useConventionalSubgenresMap } from '../../../../../hooks/useConventionalHooks'
import { BlueprintFamilyUsage } from '../../hooks/useBlueprintFamilyUsage'
import { useLiveEventsTrackingMarket } from '../../hooks/useLiveEventsTrackingMarket'
import { BlueprintEventCount } from '../BlueprintEventCount/BlueprintEventCount'
import { CustomerBlueprintLink } from '../CustomerBlueprintLink/CustomerBlueprintLink'
import { actionButtonSx } from '../helpers/actionButtonSx'

export type BlueprintFamilyGamesSortMode = 'srank' | 'name' | 'genre' | 'subgenre'
export type BlueprintFamilyGamesSortDirection = 'asc' | 'desc'

type GameBlueprintSortMode = 'name' | 'description' | 'discontinued' | 'events'
type GameBlueprintSortDirection = 'asc' | 'desc'

type BlueprintFamilyGamesTableProps = {
  usage: BlueprintFamilyUsage
}

type GameBlueprintRow = {
  trackedGame: TrackedGame
  blueprint: TrackedGameBlueprint
}

type GameBlueprintSort = { mode: GameBlueprintSortMode; direction: GameBlueprintSortDirection }

const DEFAULT_BLUEPRINT_SORT: GameBlueprintSort = { mode: 'name', direction: 'asc' }

// custom props made available to every column's labelAccessor, so the game-level-blueprint
// columns (game blueprint name, description, discontinued, events) can render as sort controls
// for the blueprints within each expanded game section, without relying on GRTable's own column
// sorting (which sorts the full flat row list and would break the game grouping). Sorting is kept
// per game (keyed by game id) so changing it in one game's blueprint list doesn't affect others.
type BlueprintColumnsCustomProps = {
  getBlueprintSort: (gameId: string) => GameBlueprintSort
  onBlueprintSortChange: (gameId: string, sortMode: GameBlueprintSortMode) => void
}

const compareBySrank = (usageA: BlueprintFamilyUsage['games'][number], usageB: BlueprintFamilyUsage['games'][number], marketIso: string): number => {
  // games without a sustained grossing rank are sorted last
  const srankA = usageA.trackedGame.game.sranks[marketIso] || Number.MAX_SAFE_INTEGER
  const srankB = usageB.trackedGame.game.sranks[marketIso] || Number.MAX_SAFE_INTEGER
  if (srankA !== srankB) {
    return srankA - srankB
  }
  return compareByName(usageA, usageB)
}

const compareByName = (usageA: BlueprintFamilyUsage['games'][number], usageB: BlueprintFamilyUsage['games'][number]): number => {
  return (usageA.trackedGame.game.name || '').localeCompare(usageB.trackedGame.game.name || '')
}

const compareByGenre = (
  usageA: BlueprintFamilyUsage['games'][number],
  usageB: BlueprintFamilyUsage['games'][number],
  marketIso: string,
  resolveGenre: (game: Game) => string
): number => {
  // games without genre data are sorted last
  const genreComparison = (resolveGenre(usageA.trackedGame.game) || '\uffff').localeCompare(resolveGenre(usageB.trackedGame.game) || '\uffff')
  if (genreComparison !== 0) {
    return genreComparison
  }
  return compareBySrank(usageA, usageB, marketIso)
}

const compareBySubgenre = (usageA: BlueprintFamilyUsage['games'][number], usageB: BlueprintFamilyUsage['games'][number], marketIso: string): number => {
  // games without subgenre data are sorted last
  const subgenreComparison = (usageA.trackedGame.game.conventionalSubgenre || '\uffff').localeCompare(usageB.trackedGame.game.conventionalSubgenre || '\uffff')
  if (subgenreComparison !== 0) {
    return subgenreComparison
  }
  return compareBySrank(usageA, usageB, marketIso)
}

const compareBlueprintByName = (blueprintA: TrackedGameBlueprint, blueprintB: TrackedGameBlueprint): number =>
  (blueprintA.names.en || '').localeCompare(blueprintB.names.en || '')

const compareBlueprintByDescription = (blueprintA: TrackedGameBlueprint, blueprintB: TrackedGameBlueprint): number => {
  // blueprints without a description are sorted last
  const descriptionComparison = (blueprintA.descriptions?.en || '\uffff').localeCompare(blueprintB.descriptions?.en || '\uffff')
  if (descriptionComparison !== 0) {
    return descriptionComparison
  }
  return compareBlueprintByName(blueprintA, blueprintB)
}

const compareBlueprintByDiscontinued = (blueprintA: TrackedGameBlueprint, blueprintB: TrackedGameBlueprint): number => {
  if (!!blueprintA.discontinued === !!blueprintB.discontinued) {
    return compareBlueprintByName(blueprintA, blueprintB)
  }
  // active blueprints are sorted before discontinued ones
  return blueprintA.discontinued ? 1 : -1
}

const compareBlueprintByEventCount = (
  blueprintA: TrackedGameBlueprint,
  blueprintB: TrackedGameBlueprint,
  eventCountByBlueprintId: Record<string, number | undefined>
): number => {
  const countA = eventCountByBlueprintId[blueprintA.blueprintId]
  const countB = eventCountByBlueprintId[blueprintB.blueprintId]
  // event counts are only known for blueprints whose game section has been expanded at least once
  // (see useEventCountByBlueprintId below), so unresolved counts are sorted last
  if (countA === countB) {
    return compareBlueprintByName(blueprintA, blueprintB)
  }
  if (countA === undefined) return 1
  if (countB === undefined) return -1
  return countA - countB
}

/**
 * Reads already-fetched event counts for the given game-level blueprint ids straight from the
 * RTK Query cache, without triggering new requests. Event counts are otherwise only fetched
 * lazily by BlueprintEventCount when a game section is expanded, so this only resolves counts
 * for blueprints that have been visible at least once - anything else stays undefined.
 */
const useEventCountByBlueprintId = (blueprintIds: string[]): Record<string, number | undefined> =>
  useAppSelector((state) => {
    const counts: Record<string, number | undefined> = {}
    blueprintIds.forEach((blueprintId) => {
      const result = coreApi.endpoints.getTrackedGameLiveEventBlueprints.select({ blueprintId, type: 'all' })(state)
      counts[blueprintId] = result.data ? new Set(result.data.evolution.map((entry) => entry.eventId)).size : undefined
    })
    return counts
  }, shallowEqual)

/**
 * Table of games whose game-level blueprints are tagged to a Blueprint Family.
 *
 * Games are rendered as collapsible sections (sorted by sustained grossing rank) and
 * the rows within each section are the game's blueprints tagged to the selected
 * Blueprint Family. Sections start collapsed so per-blueprint event counts are
 * only fetched when a game is expanded.
 */
export const BlueprintFamilyGamesTable: FC<BlueprintFamilyGamesTableProps> = ({ usage }) => {
  const { t } = useTranslation()
  const { currentMarketIso: marketIso } = useLiveEventsTrackingMarket()
  const { map: subgenresMap } = useConventionalSubgenresMap()
  const [sortMode, setSortMode] = useState<BlueprintFamilyGamesSortMode>('srank')
  const [sortDirection, setSortDirection] = useState<BlueprintFamilyGamesSortDirection>('asc')
  const [blueprintSortByGameId, setBlueprintSortByGameId] = useState<Record<string, GameBlueprintSort>>({})

  const handleSortChange = useCallback((clickedSortMode: BlueprintFamilyGamesSortMode) => {
    setSortMode((currentSortMode) => {
      setSortDirection((currentSortDirection) => (currentSortMode === clickedSortMode ? (currentSortDirection === 'asc' ? 'desc' : 'asc') : 'asc'))
      return clickedSortMode
    })
  }, [])

  const getBlueprintSort = useCallback((gameId: string): GameBlueprintSort => blueprintSortByGameId[gameId] ?? DEFAULT_BLUEPRINT_SORT, [blueprintSortByGameId])

  const handleBlueprintSortChange = useCallback((gameId: string, clickedSortMode: GameBlueprintSortMode) => {
    setBlueprintSortByGameId((current) => {
      const currentSort = current[gameId] ?? DEFAULT_BLUEPRINT_SORT
      const newDirection = currentSort.mode === clickedSortMode ? (currentSort.direction === 'asc' ? 'desc' : 'asc') : 'asc'
      return { ...current, [gameId]: { mode: clickedSortMode, direction: newDirection } }
    })
  }, [])

  // the tracked games payload carries only the subgenre id, so the genre is derived from the subgenre via the genre taxonomy
  const resolveGenre = useCallback(
    (game: Game): string => game.conventionalGenre || (game.conventionalSubgenreId ? subgenresMap[game.conventionalSubgenreId]?.genre.name || '' : ''),
    [subgenresMap]
  )

  const allBlueprintIds = useMemo(() => usage.games.flatMap((gameUsage) => gameUsage.taggedBlueprints.map((blueprint) => blueprint.blueprintId)), [usage.games])
  const eventCountByBlueprintId = useEventCountByBlueprintId(allBlueprintIds)

  // event counts are otherwise only fetched lazily when a game section is expanded (see
  // BlueprintEventCount), so sorting by events would silently have no effect for any blueprint
  // that hasn't been viewed yet. Opting into this sort mode fetches every blueprint's count
  // up front instead, so the sort always reflects real data.
  const prefetchEventCount = coreApi.usePrefetch('getTrackedGameLiveEventBlueprints')
  useEffect(() => {
    usage.games.forEach((gameUsage) => {
      const gameId = gameUsage.trackedGame.game.id
      if (getBlueprintSort(gameId).mode !== 'events') return
      gameUsage.taggedBlueprints.forEach((blueprint) => prefetchEventCount({ blueprintId: blueprint.blueprintId, type: 'all' }))
    })
  }, [usage.games, getBlueprintSort, prefetchEventCount])

  const rows: GameBlueprintRow[] = useMemo(() => {
    const directionMultiplier = sortDirection === 'desc' ? -1 : 1

    const sortedGames = [...usage.games].sort((usageA, usageB) => {
      switch (sortMode) {
        case 'name':
          return directionMultiplier * compareByName(usageA, usageB)
        case 'genre':
          return directionMultiplier * compareByGenre(usageA, usageB, marketIso, resolveGenre)
        case 'subgenre':
          return directionMultiplier * compareBySubgenre(usageA, usageB, marketIso)
        default:
          return directionMultiplier * compareBySrank(usageA, usageB, marketIso)
      }
    })

    return sortedGames.flatMap((gameUsage) => {
      const { mode: gameBlueprintSortMode, direction: gameBlueprintSortDirection } = getBlueprintSort(gameUsage.trackedGame.game.id)
      const blueprintDirectionMultiplier = gameBlueprintSortDirection === 'desc' ? -1 : 1

      return [...gameUsage.taggedBlueprints]
        .sort((blueprintA, blueprintB) => {
          switch (gameBlueprintSortMode) {
            case 'description':
              return blueprintDirectionMultiplier * compareBlueprintByDescription(blueprintA, blueprintB)
            case 'discontinued':
              return blueprintDirectionMultiplier * compareBlueprintByDiscontinued(blueprintA, blueprintB)
            case 'events':
              return blueprintDirectionMultiplier * compareBlueprintByEventCount(blueprintA, blueprintB, eventCountByBlueprintId)
            default:
              return blueprintDirectionMultiplier * compareBlueprintByName(blueprintA, blueprintB)
          }
        })
        .map((blueprint) => ({ trackedGame: gameUsage.trackedGame, blueprint }))
    })
  }, [usage.games, marketIso, sortMode, sortDirection, getBlueprintSort, eventCountByBlueprintId, resolveGenre])

  const gameUsageByGameId = useMemo(() => {
    return usage.games.reduce<{ [gameId: string]: BlueprintFamilyUsage['games'][number] }>((acc, gameUsage) => {
      acc[gameUsage.trackedGame.game.id] = gameUsage
      return acc
    }, {})
  }, [usage.games])

  const allGameIds = useMemo(() => usage.games.map((gameUsage) => gameUsage.trackedGame.game.id), [usage.games])

  // when there's only one game, GRTable renders its own (non-repeated) header instead of the
  // per-section ones, so no sectionId is available for the sort labels below - the single game's
  // id is used directly in that case instead
  const singleGameUsage = usage.games.length === 1 ? usage.games[0] : undefined
  const singleGameId = singleGameUsage?.trackedGame.game.id

  const tableColumns: GRTableColumn<GameBlueprintRow, BlueprintColumnsCustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: ({ customTableProps, sectionId }) => (
          <BlueprintColumnSortLabel sortMode="name" gameId={sectionId ?? singleGameId} customTableProps={customTableProps}>
            <Trans i18nKey="internal-live-events:game_level_blueprint" />
          </BlueprintColumnSortLabel>
        ),
        accessor: ({ row }) => <Box sx={{ pl: 2 }}>{row.blueprint.names.en}</Box>,
        cellProps: { sx: { textAlign: 'left' } },
        headerCellProps: { sx: { textAlign: 'left', minWidth: 200 } },
      },
      {
        labelAccessor: ({ customTableProps, sectionId }) => (
          <BlueprintColumnSortLabel sortMode="description" gameId={sectionId ?? singleGameId} customTableProps={customTableProps}>
            <Trans i18nKey="internal-live-events:description" />
          </BlueprintColumnSortLabel>
        ),
        accessor: ({ row }) => {
          const description = row.blueprint.descriptions?.en || ''
          if (!description) return ''

          return (
            <GRTooltip content={description}>
              <Box
                sx={{
                  pl: 2,
                  maxWidth: 300,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {description}
              </Box>
            </GRTooltip>
          )
        },
        cellProps: { sx: { textAlign: 'left' } },
        headerCellProps: { sx: { textAlign: 'left' } },
      },
      {
        labelAccessor: ({ customTableProps, sectionId }) => (
          <BlueprintColumnSortLabel sortMode="discontinued" gameId={sectionId ?? singleGameId} customTableProps={customTableProps}>
            <Trans i18nKey="live-events:live_event_evolution_type_discontinued" />
          </BlueprintColumnSortLabel>
        ),
        accessor: ({ row }) => (row.blueprint.discontinued ? <Block color="error" /> : undefined),
        cellProps: { sx: { textAlign: 'center' } },
        headerCellProps: { sx: { textAlign: 'center', width: 110, px: 2 } },
      },
      {
        labelAccessor: ({ customTableProps, sectionId }) => (
          <BlueprintColumnSortLabel sortMode="events" gameId={sectionId ?? singleGameId} customTableProps={customTableProps}>
            <Trans i18nKey="internal-live-events:events" />
          </BlueprintColumnSortLabel>
        ),
        accessor: ({ row }) => <BlueprintEventCount blueprintId={row.blueprint.blueprintId} />,
        cellProps: { sx: { textAlign: 'center' } },
        headerCellProps: { sx: { textAlign: 'center', width: 120, px: 2 } },
      },
      {
        labelAccessor: () => <Trans i18nKey="internal-live-events:actions" />,
        headerCellProps: { sx: { width: 160, px: 2 } },
        accessor: ({ row }) => {
          const gameId = row.trackedGame.game.id
          const dateParam = format(startOfToday(), 'yyyy-MM-dd')

          return (
            <Stack direction="row" spacing={1} justifyContent="center">
              <GRTooltip content={t('internal-live-events:open_internal_events_calendar')}>
                <Button
                  component={Link}
                  to={`/internal/live-events-tracking/events/${gameId}?date=${dateParam}`}
                  color="primary"
                  variant="contained"
                  sx={actionButtonSx}
                  disableElevation
                >
                  <CalendarMonth fontSize="small" />
                </Button>
              </GRTooltip>
              <CustomerBlueprintLink gameId={gameId} blueprintId={row.blueprint.blueprintId} />
              <GRTooltip content={t('internal-live-events:edit_game_level_blueprint')}>
                <Button
                  component={Link}
                  to={`/internal/live-events-tracking/events/${gameId}/blueprints/edit/${row.blueprint.blueprintId}`}
                  color="primary"
                  variant="contained"
                  sx={actionButtonSx}
                  disableElevation
                >
                  <Edit fontSize="small" />
                </Button>
              </GRTooltip>
            </Stack>
          )
        },
      },
    ],
    [t, singleGameId]
  )

  const [columns, setColumns] = useState<GRTableColumn<GameBlueprintRow, BlueprintColumnsCustomProps>[]>(tableColumns)

  // The column definitions close over `singleGameId`, so when the rendered
  // blueprint changes (different usage prop) the stateful copy GRTable sorts
  // with must be rebuilt — otherwise the sort labels keep acting on the
  // previous blueprint's game.
  useEffect(() => setColumns(tableColumns), [tableColumns])

  const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<GameBlueprintRow, BlueprintColumnsCustomProps>[]) => {
    setColumns(updatedColumns)
  }, [])

  const blueprintColumnsCustomProps: BlueprintColumnsCustomProps = useMemo(
    () => ({ getBlueprintSort, onBlueprintSortChange: handleBlueprintSortChange }),
    [getBlueprintSort, handleBlueprintSortChange]
  )

  const renderSectionHeader = useCallback(
    ({ sectionId, count }: { sectionId: string; count: number }): ReactNode => {
      const gameUsage = gameUsageByGameId[sectionId]
      if (!gameUsage) return null

      return (
        <GameSectionHeader
          trackedGame={gameUsage.trackedGame}
          blueprintCount={count}
          marketIso={marketIso}
          genreName={resolveGenre(gameUsage.trackedGame.game)}
        />
      )
    },
    [gameUsageByGameId, marketIso, resolveGenre]
  )

  // sections (and thus the repeated per-section blueprint headers) are rendered only for multiple
  // games, so the game columns header replaces the table's own header only in that case
  const hasMultipleGames = usage.games.length > 1

  return (
    <>
      {/* Section headers are rendered only when there are multiple games, so show the game info separately for a single game */}
      {singleGameUsage && (
        <Box sx={{ px: 2, py: 1.5 }}>
          <GameSectionHeader
            trackedGame={singleGameUsage.trackedGame}
            blueprintCount={singleGameUsage.taggedBlueprints.length}
            marketIso={marketIso}
            genreName={resolveGenre(singleGameUsage.trackedGame.game)}
          />
        </Box>
      )}
      {hasMultipleGames && <GameColumnsHeader sortMode={sortMode} sortDirection={sortDirection} onSortChange={handleSortChange} />}
      <TableContainer component={Box}>
        <GRTable
          rows={rows}
          columns={columns}
          customProps={blueprintColumnsCustomProps}
          rowIdKey={(row) => `${row.trackedGame.game.id}-${row.blueprint.blueprintId}`}
          onColumnsUpdated={handleColumnsUpdate}
          noRowsLabel={t('internal-live-events:no_games_for_blueprint_family')}
          getRowSectionId={(row) => row.trackedGame.game.id}
          renderSectionHeader={renderSectionHeader}
          collapsibleSections
          repeatColumnHeadersPerSection
          hideHeader={hasMultipleGames}
          defaultCollapsedSectionIds={allGameIds}
          gridlines
          striped
          hoverable
        />
      </TableContainer>
    </>
  )
}

/**
 * Sort control for a game-level-blueprint column header (game blueprint name, description,
 * discontinued, events). Sorting is applied locally within a single game's blueprint list, keyed
 * by gameId (see BlueprintColumnsCustomProps), not via GRTable's own column sorting - that would
 * sort the full flat row list, affecting every game's blueprints at once instead of just this one.
 */
const BlueprintColumnSortLabel: FC<{
  sortMode: GameBlueprintSortMode
  gameId?: string
  customTableProps?: BlueprintColumnsCustomProps
  children: ReactNode
}> = ({ sortMode, gameId, customTableProps, children }) => {
  const currentSort = gameId ? customTableProps?.getBlueprintSort(gameId) : undefined
  const isActive = currentSort?.mode === sortMode

  return (
    <SortLabel
      active={isActive}
      direction={isActive ? (currentSort?.direction as SortOrder) : SortOrder.ASC}
      onClick={() => gameId && customTableProps?.onBlueprintSortChange(gameId, sortMode)}
    >
      {children}
    </SortLabel>
  )
}

// fixed-width columns keep the badges aligned across every game section header row,
// regardless of how long an individual game's name or publisher is
const gameSectionHeaderGridSx = {
  display: 'grid',
  gridTemplateColumns: '42px minmax(160px, 320px) minmax(100px, 180px) minmax(100px, 180px) 110px 150px',
  columnGap: 2,
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
}

// flex centering removes the baseline whitespace of inline wrappers (e.g. the GRTooltip span)
// so chips and text sit vertically centered within the section header row
const gameSectionHeaderCellSx = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
}

// matches the font of the repeated blueprint column headers (which render as plain, unstyled
// text within a TableCell) instead of MUI's body2 defaults, so both header rows look identical
const gameColumnsHeaderLabelSx = {
  fontSize: '0.75rem',
  fontWeight: 'bold',
  letterSpacing: 'normal',
  lineHeight: 'inherit',
}

type GameColumnsHeaderProps = {
  sortMode: BlueprintFamilyGamesSortMode
  sortDirection: BlueprintFamilyGamesSortDirection
  onSortChange: (sortMode: BlueprintFamilyGamesSortMode) => void
}

/**
 * Header row with labels for the game section header rows. Uses the same grid as
 * GameSectionHeader so the labels line up with the values in the game rows below.
 * The game, genre, subgenre and sGR columns double as sort controls for the game sections.
 */
const GameColumnsHeader: FC<GameColumnsHeaderProps> = ({ sortMode, sortDirection, onSortChange }) => {
  const { t } = useTranslation()

  const columns: { label: string; sortMode?: BlueprintFamilyGamesSortMode }[] = [
    { label: '' }, // game icon column
    { label: t('common:game'), sortMode: 'name' },
    { label: t('common:conventional_genre'), sortMode: 'genre' },
    { label: t('common:conventional_subgenre'), sortMode: 'subgenre' },
    { label: t('common:sustained_grossing_rank_shorthand'), sortMode: 'srank' },
    { label: t('internal-live-events:linked_game_blueprints') },
  ]

  return (
    <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={gameSectionHeaderGridSx}>
        {columns.map((column, index) =>
          column.sortMode ? (
            <SortLabel
              key={index}
              active={sortMode === column.sortMode}
              direction={sortMode === column.sortMode ? (sortDirection as SortOrder) : SortOrder.ASC}
              onClick={() => onSortChange(column.sortMode as BlueprintFamilyGamesSortMode)}
            >
              <Typography variant="inherit" sx={gameColumnsHeaderLabelSx} noWrap>
                {column.label}
              </Typography>
            </SortLabel>
          ) : (
            <Typography key={index} variant="inherit" color="text.secondary" sx={gameColumnsHeaderLabelSx} noWrap>
              {column.label}
            </Typography>
          )
        )}
      </Box>
      {/* spacer matching the collapse chevron of the section header rows */}
      <Box sx={{ width: 20, flexShrink: 0 }} />
    </Box>
  )
}

const GameSectionHeader: FC<{ trackedGame: TrackedGame; blueprintCount: number; marketIso: string; genreName: string }> = ({
  trackedGame,
  blueprintCount,
  marketIso,
  genreName,
}) => {
  const { t } = useTranslation()
  const { game } = trackedGame
  const srank = game.sranks[marketIso]

  return (
    <Box sx={gameSectionHeaderGridSx}>
      <Box sx={gameSectionHeaderCellSx}>
        <GameIcon src={game.getIcon() as string} gameName={game.resolvedName} size="small" gamePlatforms={game.platforms} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body1" sx={{ fontWeight: 700 }} noWrap>
          {game.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {game.artist}
        </Typography>
      </Box>
      <Box sx={gameSectionHeaderCellSx}>
        {!!genreName && (
          <GRTooltip content={t('common:conventional_genre')} styles={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" noWrap>
              {genreName}
            </Typography>
          </GRTooltip>
        )}
      </Box>
      <Box sx={gameSectionHeaderCellSx}>
        {!!game.conventionalSubgenre && (
          <GRTooltip content={t('common:conventional_subgenre')} styles={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" noWrap>
              {game.conventionalSubgenre}
            </Typography>
          </GRTooltip>
        )}
      </Box>
      <Box sx={gameSectionHeaderCellSx}>
        {!!srank && (
          <GRTooltip content={t('common:sustained_grossing_rank')} styles={{ display: 'inline-flex', alignItems: 'center' }}>
            <Chip size="small" sx={{ margin: 0 }} label={t('internal-live-events:sustained_grossing_rank_value', { rank: srank })} />
          </GRTooltip>
        )}
      </Box>
      <Box sx={gameSectionHeaderCellSx}>
        <Chip size="small" color="primary" sx={{ margin: 0 }} label={t('internal-live-events:game_level_blueprint_count', { count: blueprintCount })} />
      </Box>
    </Box>
  )
}
